import React from 'react';
import styled from '@emotion/styled';
import Responsive from '../../../../../ui/utils/Responsive';
import { IWeather } from '@move-frontend/dap';
import {
	SecurityWaitTimesLink,
	StyledSecurityWaitTimesLink,
} from './SecurityWaitTimesLink';
import { IAirport } from '../../../../shared/api/contentful/types';
import { useTranslation } from '../../../../shared/i18n/translate/useTranslation';
import { StyledWeatherBody, Weather } from './Weather';
import { useRemoteConfigValue } from '../../../../shared/remote-config/ui/useRemoteConfigValue';
import { BusWaitTimesLink } from './BusWaitTimesLink';
import { OrderingFoodLink } from './OrderingFoodLink';

export interface IAdditionalInfoRowProps {
	airport?: IAirport;
	weather?: IWeather;
	arrival?: boolean;
}

const StyledInfoHeader = styled.h4`
	display: none;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		display: block;
		border-bottom: 2px solid #f7f7f7;
		padding-bottom: 20px;
	}
`;

const StyledAdditionalInfoRow = styled.div`
	padding: 20px;

	${StyledWeatherBody} + ${StyledSecurityWaitTimesLink} {
		margin-top: 24px;

		@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
			margin-top: 16px;
		}
	}
`;

const StyledInfoContainer = styled.div`
	display: flex;
	flex-direction: column;
	padding-top: 8px;
	> * {
		margin: 8px 0;
	}
`;

export const AdditionalInfoRow = ({
	weather,
	airport,
	arrival,
}: IAdditionalInfoRowProps) => {
	const { t } = useTranslation();
	const { value: isSecurityWaitTimesEnabled } = useRemoteConfigValue(
		'security_wait_times_enabled',
	);

	const { value: isBusWaitTimesEnabled } = useRemoteConfigValue(
		'bus_wait_times_enabled',
	);

	const { value: isOrderingFoodEnabled } = useRemoteConfigValue(
		'mobile_ordering_enabled',
	);

	const hasWeatherInfo = !!weather && !!airport;
	const hasSecurityWaitTimesLink = isSecurityWaitTimesEnabled && !arrival;
	const hasWaitBusTimes = isBusWaitTimesEnabled && !arrival;
	const hasOrderingFood = isOrderingFoodEnabled && !arrival;

	if (
		!hasWeatherInfo &&
		!hasSecurityWaitTimesLink &&
		!hasWaitBusTimes &&
		!hasOrderingFood
	) {
		return null;
	}

	return (
		<StyledAdditionalInfoRow>
			<StyledInfoHeader>
				{t('flight_details_additional_information')}
			</StyledInfoHeader>
			{hasWeatherInfo && <Weather weather={weather!} airport={airport!} />}
			<StyledInfoContainer>
				{hasWaitBusTimes && <BusWaitTimesLink />}
				{hasSecurityWaitTimesLink && <SecurityWaitTimesLink />}
				{hasOrderingFood && <OrderingFoodLink />}
			</StyledInfoContainer>
		</StyledAdditionalInfoRow>
	);
};
